<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { cardTransformer, CardFields } from '~/components/cms/CardPanel/cardHelpers'

const emit = defineEmits(['nextPage'])
type BrazeBannerItem = { brazeBanner: { placementIds?: string[]; supportingData?: any } }
const props = defineProps<{
  products?: (Omit<ProductTileProps, 'index'> | CardFields | BrazeBannerItem)[]
  totalProducts: number
  loading: boolean
  sortOptions?: SortOpion[]
  facets?: Object
  constructorEventData?: BrowseResultClickParam
  listName?: string
}>()

const isMobile = useIsMobile()
const userStore = useUserStore()
const uiStore = useUIStore()
const columnControl = useCookie('mhj-column-control', {
  default: () => 'standard',
})
const imageSizes = computed(() => {
  if (columnControl.value === 'less') return productImageSizes.gridLess
  if (columnControl.value === 'more') return productImageSizes.gridMore
  return productImageSizes.grid
})
const productListControls = ref<HTMLElement | null>(null)
const isProductListControlsVisible = ref(true)

const isProductTile = (product: any): product is Omit<ProductTileProps, 'index'> =>
  'name' in product && !('fields' in product) // Ensure product is not a CardFields type

const isBrazeBanner = (product: any): product is BrazeBannerItem => !!product?.brazeBanner

const productIndexByListIndex = computed(() => {
  let productIndex = 0
  return props.products?.map((product) => (isProductTile(product) ? productIndex++ : -1)) ?? []
})

const teaserCard = (product: any) =>
  product.fields ? product.fields.supportingData?.home?.variant?.value || 'teaser' : ''

const productsCount = computed(
  () => props.products?.filter((product) => 'name' in product && !('fields' in product)).length || 0,
)

useIntersectionObserver(productListControls, ([entry]) => {
  isProductListControlsVisible.value = !!entry?.isIntersecting
})
</script>

<template>
  <div class="product-list" data-testid="product-list" :data-test-loading="loading">
    <div class="product-list__controls-wrapper" ref="productListControls">
      <div
        class="product-list__controls"
        :class="{ 'product-list__controls--sticky': !uiStore.isHeaderHidden && !isProductListControlsVisible }"
      >
        <Filter :total="totalProducts" :facets="facets" />
        <ColumnControl />
        <SortBy :sortOptions />
      </div>
    </div>
    <div class="product-list__products" :class="`product-list__products--${columnControl}`">
      <template v-for="(product, index) in products" :key="index">
        <div v-if="isBrazeBanner(product)" class="product-list__braze-banner">
          <BrazeBanner
            :placement-ids="product.brazeBanner.placementIds"
            :supporting-data="product.brazeBanner.supportingData"
          />
        </div>
        <TeaserCardPanel
          v-else-if="teaserCard(product)"
          :cards="[{ ...cardTransformer(product as CardFields), variant: teaserCard(product) }]"
        />
        <ProductTile
          v-else-if="isProductTile(product)"
          v-bind="product"
          :list-name="listName ?? constructorEventData?.filterValue"
          :index="productIndexByListIndex[index]"
          :is-priority-image="productIndexByListIndex[index] < 8"
          :open-in-new-tab="false"
          :is-authenticated="userStore.isLoggedIn"
          :is-swipable="isMobile"
          :image-sizes="imageSizes"
          :full-title="true"
          :constructorEventData
        />
      </template>
      <ProductTileSkeleton v-if="loading" v-for="n in 24" :key="n" />
    </div>

    <section class="container">
      <div class="product-list__view-more">
        <Button v-if="totalProducts > productsCount" variant="primary" @click="emit('nextPage')">{{
          $t('search_page.product_list.button.view_more_products')
        }}</Button>

        <i18n-t keypath="search_page.product_list.item_count" tag="div" scope="global">
          <template #numberOfProductOnPage>
            <strong>{{ productsCount }}</strong>
          </template>
          <template #totalProducts>
            <strong>{{ totalProducts }}</strong>
          </template>
        </i18n-t>
      </div>
    </section>
  </div>
</template>

<style lang="postcss">
.product-list {
  .product-list__controls {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-left: var(--space-50);
    padding-right: var(--space-50);
    top: 0;
    transition: top 0.3s ease-in-out;
    @media (--tablet) {
      gap: var(--space-70);
    }
    @media (--tablet) {
      .filter {
        flex: 1;
      }
    }
    &.product-list__controls--sticky {
      @media (--mobile-only) {
        position: fixed;
        width: 100%;
        background-color: var(--surface-light-00);
        padding-top: var(--space-50);
        /* ensures that filters do not show behind menu when opened */
        z-index: calc(var(--z-header) - 1);
        top: calc(var(--header-height) + var(--header-border-height) + var(--moustache-height));
      }
    }
  }
  .product-list__products {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-auto-flow: dense;
    gap: var(--space-20);
    padding: 0 var(--space-20) var(--space-20);

    .product-list__braze-banner {
      grid-column: 1 / -1;
    }

    @media (--tablet) {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    }
    &.product-list__products--less {
      display: block;
      @media (--tablet) {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      }
    }
    &.product-list__products--more {
      @media (--tablet) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
      }
    }
  }
}

.product-list__view-more {
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-block: var(--space-100);
  gap: var(--space-50);
  border-bottom: 1px solid var(--border-break);
  padding-bottom: var(--space-100);
}
</style>
